import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { DeletePopUp, SectionPopUp } from './SidebarPopUp'
import { getOpenIds } from '../utils'
import { VuiCollapsibleMenu } from '@topkamera/viso-ui'
import SidebarShortcutPopUp from './SidebarShortcutPopUp'
import { getSidebarShortcutPageOptions } from '../shortcutPageOptions'
import './CustomSection.scss'

export const CustomSection = ({
  sideBarCustomSections = [], onSuccess, onHeaderClick, setCloseCustomIds, closeCustomIds, isCollapsed, permission, workspace, detailRole
}) => {
  const [popUpState, setPopUpState] = useState({
    isVisible: false,
    type: null,
    payload: null,
  })

  const [pageOptions, setPageOptions] = useState([])
  const [pageOptionsLoaded, setPageOptionsLoaded] = useState(false)

  const role = useMemo(() => (detailRole && detailRole.role ? detailRole.role : detailRole), [detailRole])

  const pageOptionsKey = useMemo(() => {
    const wsKey = workspace?.id || workspace?._id || workspace?.name || ''
    const roleKey = role?._id || role?.id || role || ''
    return `${wsKey}:${roleKey}`
  }, [role, workspace])

  useEffect(() => {
    setPageOptions([])
    setPageOptionsLoaded(false)
  }, [pageOptionsKey])

  const ensurePageOptionsLoaded = useCallback(async () => {
    if (pageOptionsLoaded) return

    if (!workspace || !workspace.name || !role) {
      return
    }

    try {
      const shortcutPageOptions = await getSidebarShortcutPageOptions(workspace, { role })
      setPageOptions(shortcutPageOptions)
    } catch (error) {
      setPageOptions([])
    } finally {
      setPageOptionsLoaded(true)
    }
  }, [pageOptionsLoaded, role, workspace])

  const shortcutPopUpKey = useMemo(() => {
    return `${pageOptionsKey}:${pageOptionsLoaded ? 'loaded' : 'loading'}:${(pageOptions || []).length}`
  }, [pageOptions, pageOptionsKey, pageOptionsLoaded])

  const closePopUp = () => setPopUpState({ isVisible: false, type: null, payload: null })

  const openPopUp = (type, payload) => {
    if (type === 'createShortcut' || type === 'updateShortcut') {
      ensurePageOptionsLoaded()
    }
    setPopUpState({ isVisible: true, type, payload })
  }

  const getSectionMenu = (section) => {
    const sectionId = section && section.id

    return {
      alignment: 'start',
      position: 'right',
      closeOnClick: true,
      elementId: 'popoverRoot',
      items: [
        ...(permission.includes('create') ? [{
          iconLeft: 'filledAdd',
          label: 'Add section',
          onClick: () => openPopUp('createSection', {}),
        },
        {
          iconLeft: 'addMore',
          label: 'Add shortcut',
          onClick: () => openPopUp('createShortcut', { sectionId }),
        },
        {
          isDivider: true
        }] : []),
        ...(permission.includes('edit') ? [{
          iconLeft: 'edit',
          label: 'Edit section',
          onClick: () => openPopUp('updateSection', { section }),
        }] : []),
        ...(permission.includes('delete') ? [{
          iconLeft: 'remove',
          label: 'Remove section',
          onClick: () => openPopUp('deleteSection', { sectionId }),
        }] : []),
      ]
    }
  }

  const sectionItems = (sideBarCustomSections || []).map((section) => {
    const sectionId = section && section.id
    const sectionName = (section && section.name) || ''
    const shortcuts = (section && section.shortcuts) || []
    const resolvedShortcuts = (shortcuts || []).filter((s) => s && s.id)

    return {
      actions: [
        ...(!resolvedShortcuts.length && permission.includes('create')
          ? [
            {
              icon: 'add',
              id: `add-shortcut:${sectionId || ''}`,
              label: 'Add shortcut',
            },
          ]
          : []),
        ...resolvedShortcuts.map((shortcut) => ({
          icon: (shortcut && shortcut.icon) || 'profile',
          to: shortcut && shortcut.url,
          id: shortcut && shortcut.id,
          label: (shortcut && shortcut.name) || '',
          ...(permission.includes('edit') ? {
            menu: {
              alignment: 'start',
              closeOnClick: true,
              elementId: 'popoverRoot',
              position: 'right',
              items: [
                ...(permission.includes('edit') ? [{
                  iconLeft: 'edit',
                  label: 'Edit shortcut',
                  onClick: () =>
                    openPopUp('updateShortcut', { sectionId, shortcut }),
                },
                {
                  isDivider: true,
                }] : []),
                ...(permission.includes('delete') ? [{
                  iconLeft: 'remove',
                  label: 'Remove shortcut',
                  onClick: () =>
                    openPopUp('deleteShortcut', { shortcutId: shortcut && shortcut.id, sectionId }),
                }] : []),
              ],
            },
          } : {}),
        })),
      ],
      color: 'accent0',
      header: sectionName,
      id: sectionId,
      //isDraggable: true,
      menu: permission.includes('edit') && getSectionMenu(section),
    }
  })

  const hasSections = Array.isArray(sideBarCustomSections) && sideBarCustomSections.length

  return (
    <div className="custom-section">
      <div className={classNames(
        "custom-section__list",
        (permission.includes('edit') || hasSections) && "vui-pb-15",
        isCollapsed && "custom-section__list--collapsed"
      )}>
        <VuiCollapsibleMenu
          allowMultipleOpen
          maxLabelLength={22}
          showTooltips={isCollapsed}
          dataTooltipId='customTooltipPortal'
          openIds={getOpenIds({ items: sectionItems, ids: closeCustomIds })}
          style="section"
          onHeaderClick={() => !hasSections && permission.includes('create') && openPopUp('createSection', {})}
          items={hasSections ? sectionItems : [{
            hasPermissions: permission.includes('create'),
            header: 'Add Section',
            icon: 'plusLarge',
            id: 'addSection'
          }]}
          onMenuToggle={(index, id) => onHeaderClick({
            id,
            menuIds: closeCustomIds,
            storageKey: 'sidebarClosedCustom',
            callback: setCloseCustomIds
          })
          }
          onClick={(id) => {
            if (typeof id === 'string' && id.startsWith('add-shortcut:')) {
              if (!permission.includes('create')) return
              const sectionId = id.replace('add-shortcut:', '') || null
              openPopUp('createShortcut', { sectionId })
              return
            }
          }}
        />
      </div>

      <SectionPopUp
        isVisible={popUpState.isVisible && (popUpState.type === 'createSection' || popUpState.type === 'updateSection')}
        isUpdate={popUpState.type === 'updateSection'}
        mutationArgs={
          popUpState.type === 'updateSection'
            ? {
              id: popUpState?.payload?.section?.id,
              name: popUpState?.payload?.section?.name || '',
            }
            : {}
        }
        onClose={closePopUp}
        onSuccess={onSuccess}
      />

      <SidebarShortcutPopUp
        key={shortcutPopUpKey}
        isVisible={popUpState.isVisible && (popUpState.type === 'createShortcut' || popUpState.type === 'updateShortcut')}
        isUpdate={popUpState.type === 'updateShortcut'}
        linkOptions={pageOptions}
        mutationArgs={
          popUpState.type === 'updateShortcut'
            ? {
              id: popUpState?.payload?.shortcut?.id,
              customSectionId: popUpState?.payload?.sectionId,
              name: popUpState?.payload?.shortcut?.name || '',
              icon: popUpState?.payload?.shortcut?.icon || 'profile',
              url: popUpState?.payload?.shortcut?.url || '',
              type: popUpState?.payload?.shortcut?.type || null,
            }
            : { customSectionId: popUpState?.payload?.sectionId }
        }
        onClose={closePopUp}
        onSuccess={onSuccess}
      />

      <DeletePopUp
        isVisible={popUpState.isVisible && (popUpState.type === 'deleteSection' || popUpState.type === 'deleteShortcut')}
        isShortcut={popUpState.type === 'deleteShortcut'}
        mutationArgs={
          popUpState.type === 'deleteShortcut'
            ? { id: popUpState?.payload?.shortcutId, customSectionId: popUpState?.payload?.sectionId }
            : { id: popUpState?.payload?.sectionId }
        }
        onClose={closePopUp}
        onSuccess={onSuccess}
      />
    </div>
  )
}
